import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  clearPendingHubBomId,
  getHubBom,
  pendingHubBomId,
  pendingHubProjectId,
  recordHubBomUsage,
  hubHosted,
} from "lib/hubSso";
import HubBomPicker from "components/HubBomPicker";
import { EyeIcon, DownloadIcon, FileSpreadsheet, Plus } from "lucide-react";
import iziToast from "izitoast";
import PageWrapper from "components/PageWrapper";
import { formatDateTime } from "utils/helper";
import { bomColumnKeySaveAPI, getBomStatusAPI, getUploadedBomListApi, uploadBomFileAPI } from "actions/bomActions";
import { END_POINT } from "constants/apiConstant";
import TableComponent from "components/TableComponent";
import {
  buildBomDataFromApiResponse,
  buildAllForExport,
  getExportRowsFromBomItems,
} from "utils/bomUtils";
import LoadingComponent from "components/LoadingComponent";
// import { uploadedResponse } from "./uploadedResponse";
import ExcelJS from "exceljs";
import SamiSpin from "components/SamiSpin";

const STATUS_PILL = {
  3: { label: "Completed", pill: "bg-s360-success-bg text-s360-success-ink", dot: "bg-s360-success", tile: "bg-s360-success-bg text-s360-success" },
  2: { label: "Processing", pill: "bg-s360-warning-bg text-s360-warning-ink", dot: "bg-s360-warning", tile: "bg-s360-warning-bg text-s360-warning" },
  1: { label: "Pending", pill: "bg-s360-danger-bg text-[#700A14]", dot: "bg-s360-danger", tile: "bg-s360-danger-bg text-s360-danger" },
};
const statusMeta = (status) => STATUS_PILL[status] || STATUS_PILL[1];

const BOM_EXPORT_HEADER = [
  "SKU",
  "Make",
  "Distributor",
  "Description",
  "HS Code",
  "Date Code",
  "RFQ QTY",
  "Stock",
  "MOQ",
  "MUL",
  "Price",
  "Status",
];

/** Normalize API response: support both res.data.bom_items and res.bom_items / res.fileData */
const normalizeBomResponse = (res) => {
  const data = res?.data;
  const bomItems = data?.bom_items;
  const fileData = data?.fileData;
  return {
    resForBuild: { ...res, data: { ...data, bom_items: bomItems } },
    bomItems,
    fileData: Array.isArray(fileData) ? fileData : [],
  };
};

/** Build combined header: fileData keys first, then BOM export columns */
const buildCombinedHeaders = (fileData) => {
  const fileDataHeaders =
    fileData?.length && typeof fileData[0] === "object" ? Object.keys(fileData[0]) : [];
  return [...fileDataHeaders, ...BOM_EXPORT_HEADER];
};

/** Build combined rows: one row per bom item; fileData row by index, then bom export row */
const buildCombinedRows = (fileData, fileDataHeaders, bomDataRows) => {
  return bomDataRows.map((bomRow, i) => {
    const fdRow =
      fileData[i] != null
        ? fileDataHeaders.map((k) => (fileData[i][k] != null ? String(fileData[i][k]) : ""))
        : fileDataHeaders.map(() => "");
    return [...fdRow, ...bomRow];
  });
};

const FILL_FILE_DATA = {
  type: "pattern",
  pattern: "solid",
  fgColor: { argb: "FFC6EFCE" },
};
const FILL_BOM_ITEMS = {
  type: "pattern",
  pattern: "solid",
  fgColor: { argb: "FFFFEB9C" },
};

const BLACK_BORDER = {
  top: { style: "thin", color: { argb: "FF000000" } },
  left: { style: "thin", color: { argb: "FF000000" } },
  bottom: { style: "thin", color: { argb: "FF000000" } },
  right: { style: "thin", color: { argb: "FF000000" } },
};

const downloadExcelWithColors = async (
  fullHeaders,
  combinedRows,
  fileDataHeaderCount,
  filename,
) => {
  const wb = new ExcelJS.Workbook();
  const ws = wb.addWorksheet("BOM Consolidated", { views: [{ state: "frozen", ySplit: 1 }] });
  const headerRow = ws.addRow(fullHeaders);
  headerRow.font = { bold: true };
  headerRow.eachCell((cell, colNumber) => {
    cell.fill = colNumber <= fileDataHeaderCount ? FILL_FILE_DATA : FILL_BOM_ITEMS;
    cell.border = BLACK_BORDER;
  });
  ws.addRows(combinedRows);
  ws.eachRow((row, rowNumber) => {
    if (rowNumber === 1) return;
    row.eachCell((cell, colNumber) => {
      cell.fill = colNumber <= fileDataHeaderCount ? FILL_FILE_DATA : FILL_BOM_ITEMS;
      cell.border = BLACK_BORDER;
    });
  });
  const buf = await wb.xlsx.writeBuffer();
  const blob = new Blob([buf], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const downloadCsv = (arrayList, filename = "bom-consolidated.csv") => {
  const csvContent = arrayList
    .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(","))
    .join("\n");
  const csvDataUri = `data:text/csv;charset=utf-8,${encodeURIComponent(csvContent)}`;
  const link = document.createElement("a");
  link.setAttribute("href", csvDataUri);
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const UploadedBomPage = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState([]);
  const navigate = useNavigate();
  const [selectedFiles, setSelectedFiles] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);
  const [importingHubBom, setImportingHubBom] = useState(false);

  // One import path for both ways a Hub board arrives: sent from the Hub as
  // ?bom=<id>, or picked here in the shared picker. Either way it was chosen,
  // filed and column-mapped in the Hub already, so it is imported by id — the
  // buyer is never asked to find, re-upload or re-map the file the suite is
  // already holding.
  //
  // It goes in through the ordinary upload endpoint as a CSV built from the
  // Hub's lines, so the sourcing and PO flow behind it are the exact same code
  // path an uploaded file takes. Nothing new to keep in step.
  const importHubBomById = useCallback(async (bomId, cancelledRef) => {
    const cancelled = () => Boolean(cancelledRef && cancelledRef.cancelled);
    {
      setImportingHubBom(true);
      try {
        // A board sent twice must OPEN, not import twice. If an upload here
        // already came from this Hub board, go straight to its BOM tool — the
        // sourcing already done is the asset, and a duplicate would split it.
        const existing = await getUploadedBomListApi();
        if (cancelled()) return;
        const uploads = existing?.data?.results || existing?.data || [];
        const prior = Array.isArray(uploads) ? uploads.find((r) => r.hub_bom_id === bomId) : null;
        if (prior?.unique_id) {
          clearPendingHubBomId();
          navigate(`/bom-tool/${prior.unique_id}`);
          return;
        }

        const bom = await getHubBom(bomId);
        const lines = (bom && (bom.lines || bom.items)) || [];
        if (cancelled()) return;

        if (!lines.length) {
          iziToast.warning({
            title: "Nothing to import",
            message: "That Semi360 BOM has no lines.",
            position: "topCenter",
          });
          return;
        }

        // "Line Key" is the Hub's identity for each line (hub360
        // docs/bom-line-key.md). The backend stores it on the item, reads the
        // line's uploaded fields back from the Hub by it, and sends it on the
        // order's derive, so the Hub never has to guess which line was bought.
        const header = "Part Number,Quantity,Line Key\n";
        const rows = lines
          .map((l) => {
            const mpn = String(l.mpn || l.part_number || l.partNumber || "").trim();
            const qty = l.qty ?? l.quantity ?? l.qty_required ?? "";
            const key = String(l.lineKey || "").replace(/[^A-Za-z0-9_-]/g, "");
            if (!mpn) return null;
            // Quote the MPN: manufacturer part numbers legitimately contain commas.
            return `"${mpn.replace(/"/g, '""')}",${qty},${key}`;
          })
          .filter(Boolean)
          .join("\n");

        const name = (bom.name || `semi360-bom-${bomId}`).replace(/[^\w.-]+/g, "_");
        const file = new File([header + rows], `${name}.csv`, { type: "text/csv" });

        const response = await uploadBomFileAPI([file], {
          hub_bom_id: bomId,
          hub_project_id: pendingHubProjectId(),
        });
        if (cancelled()) return;

        if (response.status) {
          // Tell the Hub this board reached QBuy360, so the board's lifecycle
          // track shows it rather than claiming nothing happened.
          recordHubBomUsage(bomId, response.data?.unique_id || "");
          clearPendingHubBomId();

          // The Hub already asked which column is the MPN and which is the
          // quantity, and _bom_full() hands us the parsed lines. Showing the
          // mapping screen again would ask the buyer a question the suite has
          // already answered — and let them answer it differently. We wrote the
          // CSV above, so we know its headers exactly; save them and go straight
          // to sourcing.
          const uniqueId = response.data?.unique_id;
          const mapped = await bomColumnKeySaveAPI({
            uniqueId,
            partNumberKey: "Part Number",
            qtyKey: "Quantity",
            lineKeyKey: "Line Key",
            rowIndex: 0,
          });
          if (cancelled()) return;

          // If saving the mapping fails, fall back to the mapping screen rather
          // than stranding the buyer on a BOM with no lines.
          navigate(mapped.status ? `/bom-tool/${uniqueId}` : `/bom-column-mapping/${uniqueId}`);
        } else {
          iziToast.error({
            title: "Import failed",
            message: response.message || "Could not import that Semi360 BOM.",
            position: "topCenter",
          });
        }
      } catch (err) {
        if (!cancelled()) {
          iziToast.error({
            title: "Import failed",
            message: "Could not reach Semi360 to import that BOM.",
            position: "topCenter",
          });
        }
      } finally {
        if (!cancelled()) setImportingHubBom(false);
      }
    }
  }, [navigate]);

  // Claim a board sent from the Hub (?bom=<id>) on arrival.
  useEffect(() => {
    const bomId = pendingHubBomId();
    if (!bomId) return undefined;
    const ref = { cancelled: false };
    importHubBomById(bomId, ref);
    return () => {
      ref.cancelled = true;
    };
  }, [importHubBomById]);

  // The shared picker fires the same import; a picked board and a sent board
  // take the identical route in.
  const onHubPick = useCallback(
    (b) => {
      if (b && b.id) importHubBomById(b.id, null);
    },
    [importHubBomById],
  );

  // Direct file upload here is retired (api/docs/lines-from-hub.md, P10): the
  // backend now answers any upload with no hub_bom_id with 410 upload_via_hub.
  // "New upload" no longer opens a native file picker — it just points at the
  // Hub picker section below, which already covers upload (it owns "list,
  // pick, upload, and the ONE column-mapping step the suite runs", per
  // components/HubBomPicker.jsx).
  const hubSectionRef = useRef(null);
  const scrollToHubUpload = () => {
    hubSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const handleDownload = async (row) => {
    const id = row?.unique_id;
    if (!id) return;
    setDownloadingId(id);
    setLoading(true);
    try {
      const res = await getBomStatusAPI(id);
      // const res = uploadedResponse;
      console.log("UploadedBomPage Response:STEP-1===>", res);
      if (!res?.status) {
        iziToast.error({
          title: "Error",
          message: res?.message || "Failed to load BOM data",
          position: "topCenter",
        });
        return;
      }
      const { resForBuild, bomItems, fileData } = normalizeBomResponse(res);
      console.log("UploadedBomPage Response:STEP-2===>", resForBuild, fileData);
      if (!bomItems?.length) {
        iziToast.warning({
          title: "No data",
          message: "No BOM items to download",
          position: "topCenter",
        });
        return;
      }
      const bomData = buildBomDataFromApiResponse(resForBuild);
      if (!bomData?.bomItems?.length) {
        iziToast.warning({
          title: "No data",
          message: "No BOM items to download",
          position: "topCenter",
        });
        return;
      }
      const all = buildAllForExport(bomData);
      const dataRows = getExportRowsFromBomItems(bomData, all);
      const fileDataHeaders =
        fileData?.length && typeof fileData[0] === "object" ? Object.keys(fileData[0]) : [];
      const fullHeaders = buildCombinedHeaders(fileData);
      const combinedRows = buildCombinedRows(fileData, fileDataHeaders, dataRows);
      const baseName = (row?.file_name || "bom").replace(/\s+/g, "-").replace(/\.[^/.]+$/, "");
      const fileName = `${baseName}-consolidated.xlsx`;
      await downloadExcelWithColors(fullHeaders, combinedRows, fileDataHeaders.length, fileName);
      iziToast.success({
        title: "Downloaded",
        message: "BOM export downloaded successfully",
        position: "topCenter",
      });
    } catch (err) {
      console.error("BOM download error", err);
      iziToast.error({
        title: "Error",
        message: err?.message || "Failed to download BOM",
        position: "topCenter",
      });
    } finally {
      setDownloadingId(null);
      setLoading(false);
    }
  };

  const columnHeaders = [
    {
      dataField: "file_name",
      name: "File Name",
      headerAlign: "left",
      style: { textAlign: "left" },
      formatter: (col, row) => {
        const meta = statusMeta(row?.status);
        return (
          <div className="flex items-center gap-3">
            <span
              className={`w-9 h-9 rounded-lg inline-flex items-center justify-center shrink-0 ${meta.tile}`}
            >
              <FileSpreadsheet className="w-4 h-4" />
            </span>
            <div className="flex flex-col min-w-0">
              <span className="font-semibold text-s360-fg1 truncate">{row?.file_name}</span>
              <span className="font-mono text-xs text-s360-fg3 truncate">{row?.unique_id}</span>
            </div>
          </div>
        );
      },
    },
    {
      dataField: "created_at",
      name: "Uploaded Date",
      headerAlign: "left",
      style: { textAlign: "left" },
      formatter: (col, row) => {
        return formatDateTime(row?.created_at);
      },
    },
    {
      dataField: "updated_at",
      name: "Last Modified",
      headerAlign: "left",
      style: { textAlign: "left" },
      formatter: (col, row) => {
        return formatDateTime(row?.updated_at);
      },
    },
    {
      dataField: "status",
      name: "Status",
      headerAlign: "center",
      style: { textAlign: "center" },
      formatter: (col, row) => {
        const meta = statusMeta(row?.status);
        return (
          <span
            className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded text-xs font-semibold ${meta.pill}`}
          >
            <span className={`w-1.5 h-1.5 rounded-full ${meta.dot}`} />
            {meta.label}
          </span>
        );
      },
    },
    {
      dataField: "action",
      name: "Actions",
      headerAlign: "center",
      style: { textAlign: "center" },
      formatter: (col, row) => {
        return (
          <div className="flex justify-center gap-1.5">
            <button
              className="w-8 h-8 inline-flex items-center justify-center rounded-md border border-s360-stroke1 text-s360-violet hover:bg-s360-violet-50"
              onClick={() => navigate(`/bom-tool/${row.unique_id}`)}
              title="View BOM"
            >
              <EyeIcon className="w-4 h-4" />
            </button>
            <button
              className="w-8 h-8 inline-flex items-center justify-center rounded-md border border-s360-stroke1 text-s360-fg2 hover:bg-s360-bg2 disabled:opacity-50"
              onClick={() => downloadingId !== row.unique_id && handleDownload(row)}
              disabled={downloadingId === row.unique_id}
              title={downloadingId === row.unique_id ? "Downloading..." : "Download Excel"}
            >
              {downloadingId === row.unique_id ? (
                <SamiSpin className="w-4 h-4 animate-spin" />
              ) : (
                <DownloadIcon className="w-4 h-4" />
              )}
            </button>
          </div>
        );
      },
    },
  ];

  return (
    <PageWrapper showHeader={false}>
      {/* min-h-full, not h-full: the column fills the viewport but is allowed to
          exceed it. Clamped to exactly h-full, everything here had to fit
          whatever height the viewport happened to be — and the Hub picker
          below is as tall as the org's project list, so on a short window
          there was nothing left to give the list. */}
      <div className="flex min-h-full flex-col gap-4">
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div className="flex flex-col gap-1.5">
            <h1 className="text-[28px] leading-[34px] font-bold tracking-[-0.015em] text-s360-ink">
              Uploaded BOMs
            </h1>
            <span className="text-sm text-s360-fg3">
              View, track, and export the BOM files you've uploaded for distributor analysis.
            </span>
          </div>
          {hubHosted() && (
            <button
              type="button"
              onClick={scrollToHubUpload}
              className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-md bg-s360-violet text-white text-[13px] font-semibold hover:bg-s360-violet-600 cursor-pointer"
            >
              <Plus className="w-3.5 h-3.5" /> New upload
            </button>
          )}
        </div>

        {hubHosted() && (
          <div ref={hubSectionRef} className="rounded-lg border border-s360-stroke1 bg-white p-4">
            <p className="text-sm text-s360-fg3 m-0">
              Pick a board from your Hub, or upload one — it is stored there, mapped once, and
              every product reads the same lines.
            </p>
            {importingHubBom ? (
              <p className="text-sm text-s360-fg3 mt-3 mb-0">Importing from your Hub…</p>
            ) : (
              <div className="mt-3">
                <HubBomPicker onPick={onHubPick} />
              </div>
            )}
          </div>
        )}

        {/* The list is still the region that grows and scrolls when there is
            room for it. What it may NOT do is disappear: with min-h-0 it was a
            flex item free to shrink to nothing, and that is exactly what it did
            — 3px of top border, the whole "Uploaded BOMs" table (the subject of
            the page) collapsed to a sliver below the fold with no way to reach
            it. The floor keeps roughly six rows on screen; past that the page
            scrolls. */}
        <div className="bg-white rounded-md shadow-sm border-t-[3px] border-t-s360-violet flex-1 min-h-[320px] overflow-auto">
          {/* type=bom keeps part number searches out of this list; they are
              listed as history on the Search page instead. */}
          <TableComponent
            apiRoute={`${END_POINT.GET_UPLOADED_BOM_LIST}?type=bom`}
            columnHeaders={columnHeaders}
            searchPlaceholder="Search by file name…"
            rowHoverClass="hover:bg-s360-bg2"
            noBorderRadius
          />
        </div>
      </div>
    </PageWrapper>
  );
};

export default UploadedBomPage;
